import { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

// --- SUPABASE SETUP ---
const supabaseUrl = "https://xennaetkdnxpyheplbfm.supabase.co";
const supabaseKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inhlbm5hZXRrZG54cHloZXBsYmZtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY5MjM5NDQsImV4cCI6MjA5MjQ5OTk0NH0.RyKCX5wxjp046u7hTpPpFjY-WrS_1uB8tYKTc28ZuGI";
const supabase = createClient(supabaseUrl, supabaseKey);

// --- AUDIO SYSTEM ---
let audioCtx = null;
const initAudio = () => {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    audioCtx = new AC();
  }
  if (audioCtx.state === "suspended") audioCtx.resume();
};
const playSound = (type) => {
  if (!audioCtx) return;
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  if (type === "correct") {
    osc.type = "sine";
    osc.frequency.setValueAtTime(440, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(
      880,
      audioCtx.currentTime + 0.12,
    );
    gain.gain.setValueAtTime(0.25, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.3);
  } else {
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(160, audioCtx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(80, audioCtx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.25, audioCtx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.3);
    osc.start();
    osc.stop(audioCtx.currentTime + 0.3);
  }
};

// --- TYPEWRITER ---
const Typewriter = ({ text, speed = 18 }) => {
  const [displayed, setDisplayed] = useState("");
  useEffect(() => {
    setDisplayed("");
    let i = 0;
    const iv = setInterval(() => {
      setDisplayed(text.substring(0, i + 1));
      i++;
      if (i >= text.length) clearInterval(iv);
    }, speed);
    return () => clearInterval(iv);
  }, [text, speed]);
  return <span style={{ whiteSpace: "pre-wrap" }}>{displayed}</span>;
};

// --- LOOPING TYPEWRITER ---
const LoopingTypewriter = ({
  phrases,
  typeSpeed = 60,
  deleteSpeed = 25,
  delay = 5000,
}) => {
  const [text, setText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);

  useEffect(() => {
    const i = loopNum % phrases.length;
    const fullText = phrases[i];

    let timer;
    if (isDeleting) {
      timer = setTimeout(() => {
        setText(fullText.substring(0, text.length - 1));
        if (text === "") {
          setIsDeleting(false);
          setLoopNum(loopNum + 1);
        }
      }, deleteSpeed);
    } else {
      if (text === fullText) {
        timer = setTimeout(() => setIsDeleting(true), delay);
      } else {
        timer = setTimeout(() => {
          setText(fullText.substring(0, text.length + 1));
        }, typeSpeed);
      }
    }
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, phrases, typeSpeed, deleteSpeed, delay]);

  return <span style={{ whiteSpace: "pre-wrap" }}>{text}</span>;
};

// --- SCENARIOS ---
const SCENARIOS = [
  {
    id: 1,
    round: 1,
    type: "SMS",
    sender: "Super-Alert",
    content:
      "New ATO policy: You may be eligible to withdraw up to $10,000 from your superannuation early for cost-of-living relief. Check eligibility: http://mygov-portal-super.com",
    isScam: true,
    hint: "Agent Kal says: The ATO does allow early access to super in very specific cases, but would they invite you to do it via a text link?",
    explanation:
      "Scam! This is a 'Early Access' fraud. You can only apply for early release of super through your official myGov account in very limited circumstances. The ATO never sends SMS links to 'withdraw' super.",
    tip: "Never use a link to access your super. Always log in to myGov and go to the 'Super' section of the ATO Online Services.",
    intelLink: {
      label: "Early access to Super",
      url: "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/withdrawing-and-using-your-super/early-access-to-super",
    },
  },
  {
    id: 2,
    round: 1,
    type: "EMAIL",
    sender: "noreply@ato.gov.au",
    subject: "Your tax return has been processed",
    content:
      "Your tax return for 2024–25 has been processed. Log in to myGov to view your notice of assessment. No action is required unless you'd like to update your bank details.",
    isScam: false,
    hint: "Agent Kal says: Notice what this message is asking you to do — or rather, what it's not asking you to do. How does the real ATO usually want you to access your account?",
    explanation:
      "Legit! There are NO links in this email. The real ATO tells you to go and log in to myGov yourself — they never send a login link straight to your inbox.",
    tip: "Genuine ATO emails direct you to myGov or ato.gov.au — they don't include login links or ask for personal details by reply.",
  },
  {
    id: 3,
    round: 1,
    type: "VOICEMAIL",
    sender: "Unknown Number",
    content:
      '"This is the Australian Taxation Office. You have an outstanding debt of $4,000. Failure to pay may result in your arrest. To avoid further action, you must make payment today using gift cards. Press 1 to speak with an officer."',
    isScam: true,
    hint: "Agent Kal says: Think about how real government debt is communicated. Would they ever tell you to pay immediately through instructions in a voicemail?",
    intelLink: {
      label: "Phone scams",
      url: "https://www.ato.gov.au/online-services/scams-cyber-safety-and-identity-protection/verify-or-report-a-scam#Phonescams",
    },
    explanation:
      "Massive scam. The ATO will never threaten arrest, demand same-day payment, or ask you to pay using gift cards. Legitimate debt is always communicated in writing through your myGov account — never through voicemail ultimatums.",
    tip: "The ATO will NEVER demand gift card payment or threaten immediate arrest. Check real debt notices in myGov Online Services.",
  },
  {
    id: 4,
    round: 2,
    type: "SOCIAL MEDIA DM",
    sender: "@ATO_Official_Dave",
    content:
      "Hi, I work with the ATO support team. We’re helping people maximise their super returns this month. If you’d like assistance, send your TFN and we can review your account.",
    isScam: true,
    hint: "Agent Kal says: Think about the platform being used here. Is social media a secure, official channel for a government employee to ask for your TFN?",
    intelLink: {
      label: "Social media scams",
      url: "https://www.ato.gov.au/online-services/scams-cyber-safety-and-identity-protection/verify-or-report-a-scam#Socialmediascams",
    },
    explanation:
      "Scam! This message uses a fake “support team” identity to create trust and pressure you into sharing your Tax File Number (TFN). Real government staff from the ATO will never contact you via social media or request sensitive details through DMs.",
    tip: "Never share your TFN or personal details over social media. Government services only operate through official channels like myGov or ATO Online Services.",
  },
  {
    id: 10,
    round: 2,
    type: "EMAIL",
    sender: "recovery@super-find.com",
    subject: "Lost Superannuation Found",
    content:
      "Our records indicate you may have unclaimed superannuation valued at $3,241. To release these funds, please confirm your identity by providing your TFN and bank details using the secure form below.",
    isScam: true,
    hint: "Agent Kal says: Paying or giving sensitive details to a random third party to find lost super? Sounds fishy.",
    intelLink: {
      label: "TFN (Tax File Number)",
      url: "https://www.ato.gov.au/individuals-and-families/tax-file-number",
    },
    explanation:
      "Scam! This message uses “unclaimed super” as bait and pressures you to submit personal details. Real lost super can only be searched for free through official government services, not third-party email requests.",
    tip: "Always use myGov or the official ATO website to check for lost super — it's free and secure. Never provide your TFN in response to an unsolicited email or to an unknown website.",
  },
  {
    id: 5,
    round: 2,
    type: "myGov INBOX",
    sender: "Australian Taxation Office",
    subject: "Income statement now available",
    content:
      "Your income statement from your employer is now finalised in ATO Online Services. You can now complete your tax return. Log in to ATO Online Services through myGov to get started.",
    isScam: false,
    hint: "Agent Kal says: Where exactly did you receive this message? That's the biggest clue.",
    explanation:
      "Legit! The myGov Inbox is a secure, government-controlled environment. Messages delivered directly inside your myGov account are the safest way the ATO communicates with you.",
    tip: "Your myGov Inbox is a protected communication channel — always check there first for official ATO notices.",
  },
  {
    id: 6,
    round: 2,
    type: "SMS",
    sender: "myGov",
    content:
      "myGov: Your one-time code is 847291. Do not share this code with anyone, including myGov staff. If you did not request this, call 132 307.",
    isScam: false,
    hint: "Agent Kal says: Read the security instructions included in the message carefully.",
    explanation:
      "Legit! This is a real two-factor authentication (2FA) code. It includes a strict warning not to share it. Scammers will often call you and try to trick you into reading this code aloud.",
    tip: "2FA via SMS is a real myGov security feature. Never read your one-time code to anyone who calls you.",
  },
  {
    id: 7,
    round: 3,
    type: "EMAIL",
    sender: "security@ato.gov.au.notifications.net",
    subject: "Tax File Number Suspended",
    content:
      "Your TFN has been temporarily suspended due to suspicious activity. To reactivate your account, confirm your identity using the secure portal below. You have 48 hours before your TFN is permanently deleted.",
    isScam: true,
    hint: "Agent Kal says: Don't let the 48-hour countdown distract you. Look at the tail end of the sender's email address. Also is a TFN temporary or permanent?",
    intelLink: {
      label: "TFN (Tax File Number)",
      url: "https://www.ato.gov.au/individuals-and-families/tax-file-number",
    },
    explanation:
      "Scam! This message impersonates the Australian Taxation Office and uses fear-based language about account restrictions. It also falsely claims a TFN can be suspended or deleted, which is not possible.",
    tip: "Your TFN is permanent — it never expires, gets suspended, or gets deleted. Scammers use fake urgency and countdown timers to panic you into acting before you think. When in doubt, log in to myGov directly yourself and check for any real notices there.",
  },
  {
    id: 11,
    round: 3,
    type: "SMS",
    sender: "AustralianSuper",
    content:
      "AustralianSuper: We’ve noticed unusual activity on your account. Please verify your details to avoid temporary suspension: http://australiansuper-verify.com",
    isScam: true,
    hint: "Agent Kal says: Read the URL very carefully. Is that the real AustralianSuper website?",
    intelLink: {
      label: "SMS scams",
      url: "https://www.ato.gov.au/online-services/scams-cyber-safety-and-identity-protection/verify-or-report-a-scam#SMSscams",
    },
    explanation:
      "Scam! This is a subdomain trick. The real domain is australiansuper.com. Scammers register domains with hyphens to look legitimate.",
    tip: "Never click links in SMS. Always log in directly through your fund's official app or website.",
  },
  {
    id: 8,
    round: 3,
    type: "ATO APP PUSH",
    sender: "ATO App",
    content:
      "Your super balance has been updated. Total across 2 funds: $14,820.00. You may have a lost super account. Open the ATO app to review your accounts and explore consolidation options.",
    isScam: false,
    hint: "Agent Kal says: Which application is sending this notification, and how are you accessing it?",
    intelLink: {
      label: "ATO app",
      url: "https://www.ato.gov.au/online-services/online-services-for-individuals-and-sole-traders/ato-app",
    },
    explanation:
      "Legit! The official ATO App pulls your super data directly from ATO systems. Because you open the app directly from your phone's secure environment, it's safe from phishing links.",
    tip: "The ATO app lets you track super, check tax returns, and find lost accounts — all in one secure place.",
  },
  {
    id: 12,
    round: 3,
    type: "myGov INBOX",
    sender: "Australian Taxation Office",
    subject: "Super guarantee contribution",
    content:
      "Your employer has reported paying your super guarantee for the quarter. Log in to your super fund to verify receipt.",
    isScam: false,
    hint: "Agent Kal says: This came straight to your myGov inbox. Is there any malicious link?",
    explanation:
      "Legit! Safe because it's delivered securely inside your myGov inbox. Plus, it tells you to log in yourself rather than providing a link.",
    tip: "Your myGov inbox is a protected channel. Always check there for official correspondence.",
  },
  {
    id: 9,
    round: 3,
    type: "EMAIL",
    sender: "refunds@ato.gov.au",
    subject: "You are owed a refund – bank details needed",
    content:
      "Following a recent review, you are entitled to a refund of $1,247. To process this payment, please reply with your full name, date of birth, and bank account details.",
    isScam: true,
    hint: "Agent Kal says: The sender address looks convincing — but did you know email addresses can be faked entirely? And think about where the ATO already keeps your bank details...",
    intelLink: {
      label: "Email scams",
      url: "https://www.ato.gov.au/online-services/scams-cyber-safety-and-identity-protection/verify-or-report-a-scam#Emailscams",
    },
    explanation:
      "Scam — even though the address looks real. Scammers can 'spoof' sender addresses to display anything, including ato.gov.au. The ATO already has your bank details linked via myGov and will never ask you to reply to an email with your BSB and account number.",
    tip: "Real refunds are deposited automatically to the account linked in myGov. Never email financial details to anyone.",
  },
];

const ROUND_LABELS = ["NOVICE DECODER", "SCAM DETECTIVE", "CYBER AGENT"];

const getTypeColor = (type) => {
  if (type.includes("SMS")) return "#3b82f6";
  if (type.includes("EMAIL")) return "#8b5cf6";
  if (type.includes("VOICE")) return "#ec4899";
  if (type.includes("myGov")) return "#10b981";
  if (type.includes("APP")) return "#f5c400";
  if (type.includes("SOCIAL")) return "#f58400";
  return "#6b7280";
};

const getTypeIcon = (type) => {
  if (type.includes("SMS")) return "💬";
  if (type.includes("EMAIL")) return "📧";
  if (type.includes("VOICE")) return "📞";
  if (type.includes("myGov")) return "🔒";
  if (type.includes("APP")) return "📱";
  if (type.includes("SOCIAL")) return "📲";
  return "📄";
};

export default function ScamDefender() {
  const [screen, setScreen] = useState("name"); // Starts at Name entry
  const [operativeName, setOperativeName] = useState("");
  const [nameError, setNameError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [globalLeaderboard, setGlobalLeaderboard] = useState([]);
  const [isFetchingLeaderboard, setIsFetchingLeaderboard] = useState(false);

  // Scoring & Stats State
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [answers, setAnswers] = useState([]);

  // Timer State
  const [timeLeft, setTimeLeft] = useState(60);
  const [lastScoreBreakdown, setLastScoreBreakdown] = useState(null);

  // UX State
  const [lastCorrect, setLastCorrect] = useState(null);
  const [isTimeout, setIsTimeout] = useState(false);
  const [animIn, setAnimIn] = useState(true);
  const [shake, setShake] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [showRestartAlert, setShowRestartAlert] = useState(false);

  // Leaderboard State
  const [leaderboard, setLeaderboard] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const current = SCENARIOS[currentIndex];
  const total = SCENARIOS.length;

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };
  const triggerPulse = () => {
    setPulse(true);
    setTimeout(() => setPulse(false), 600);
  };

  // --- TIMER LOGIC ---
  useEffect(() => {
    let timer;
    if (screen === "game" && animIn) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            handleAnswer(null, true); // Trigger timeout
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [screen, animIn, currentIndex]);

  // --- LEADERBOARD LOGIC ---
  useEffect(() => {
    if (screen === "results") {
      const submitAndFetch = async () => {
        setIsSubmitting(true);
        const correctAnswers = answers.filter((a) => a.correct).length;

        // Insert new score
        await supabase.from("leaderboard").insert([
          {
            operative_name: operativeName,
            score: score,
            correct: correctAnswers,
            total: total,
            max_streak: maxStreak,
          },
        ]);

        const { data } = await supabase
          .from("leaderboard")
          .select("*")
          .order("score", { ascending: false })
          .limit(10);

        setLeaderboard(data || []);
        setIsSubmitting(false);
      };
      submitAndFetch();
    }
  }, [screen]);

  const handleAuthenticate = async () => {
    if (!operativeName) return;
    setIsVerifying(true);
    setNameError("");

    try {
      // Check if name is already in the database
      const { data, error } = await supabase
        .from("leaderboard")
        .select("operative_name")
        .eq("operative_name", operativeName)
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        setNameError("⚠ ID TAKEN. CHOOSE ANOTHER OPERATIVE NAME.");
      } else {
        setScreen("intro"); // Name is free, proceed!
      }
    } catch (err) {
      console.error(err);
      setNameError("⚠ DATABASE ERROR. TRY AGAIN.");
    } finally {
      setIsVerifying(false);
    }
  };

  const openLeaderboard = async () => {
    setShowLeaderboardModal(true);
    setIsFetchingLeaderboard(true);
    try {
      const { data, error } = await supabase
        .from("leaderboard")
        .select("*")
        .order("score", { ascending: false })
        .limit(100);
      if (data) setGlobalLeaderboard(data);
    } catch (err) {
      console.error(err);
    } finally {
      setIsFetchingLeaderboard(false);
    }
  };

  const handleStart = () => {
    initAudio();
    setScreen("game");
  };

  const handleAnswer = (guessScam, timeout = false) => {
    let correct = false;
    let pts = 0;
    let basePts = 0;
    let timeBonus = 0;
    let streakBonus = 0;

    if (!timeout) {
      correct = guessScam === current.isScam;
    }

    const newStreak = correct ? streak + 1 : 0;

    if (correct) {
      basePts = 100;
      timeBonus = Math.floor((timeLeft / 60) * 100);
      pts = basePts + timeBonus;

      // Streak bonus begins on the 3rd correct answer in a row
      if (newStreak >= 3) {
        streakBonus = 25;
        pts += streakBonus;
      }
    }

    setScore((s) => s + pts);
    setStreak(newStreak);
    setMaxStreak((m) => Math.max(m, newStreak));
    setLastCorrect(correct);
    setIsTimeout(timeout);

    setLastScoreBreakdown({
      base: basePts,
      time: timeBonus,
      streak: streakBonus,
      total: pts,
    });

    setAnswers((a) => [
      ...a,
      { id: current.id, correct, isScam: current.isScam, timeout },
    ]);

    if (correct) {
      triggerPulse();
      playSound("correct");
    } else {
      triggerShake();
      playSound("wrong");
    }
    setScreen("feedback");
  };

  const handleNext = () => {
    setAnimIn(false);
    setTimeout(() => {
      const next = currentIndex + 1;
      setTimeLeft(60); // Reset timer for next question
      if (next >= total) {
        setScreen("results");
      } else if (SCENARIOS[next].round > current.round) {
        setCurrentIndex(next);
        setScreen("roundbreak");
      } else {
        setCurrentIndex(next);
        setScreen("game");
      }
      setAnimIn(true);
    }, 180);
  };

  const resetGame = () => {
    setScreen("name");
    setOperativeName("");
    setCurrentIndex(0);
    setScore(0);
    setStreak(0);
    setMaxStreak(0);
    setLastCorrect(null);
    setAnswers([]);
    setTimeLeft(60);
    setAnimIn(true);
  };

  const handleRestart = () => {
    setShowRestartAlert(true);
  };

  const getRank = () => {
    const pct = score / (total * 225); // Adjusted max possible per question ~225
    if (pct >= 0.8)
      return {
        title: "ELITE CYBER AGENT",
        color: "#00f5a0",
        desc: "Outstanding speed and accuracy. Unbeatable.",
      };
    if (pct >= 0.5)
      return {
        title: "FRAUD INVESTIGATOR",
        color: "#f5c400",
        desc: "Solid instincts. A few tricky ones caught you.",
      };
    if (pct >= 0.3)
      return {
        title: "TRAINEE",
        color: "#f58400",
        desc: "You blocked some, but scammers got through.",
      };
    return {
      title: "COMPROMISED",
      color: "#f53b3b",
      desc: "Time to study up on the ATO App and myGov.",
    };
  };

  const S = {
    root: {
      minHeight: "100vh",
      background: "#050a0f",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'Courier New', monospace",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
      boxSizing: "border-box",
    },
    grid: {
      position: "fixed",
      inset: 0,
      backgroundImage:
        "linear-gradient(rgba(0,245,160,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,245,160,0.04) 1px, transparent 1px)",
      backgroundSize: "30px 30px",
      pointerEvents: "none",
      zIndex: 0,
    },
    phone: {
      width: "100%",
      maxWidth: "420px",
      minHeight: "680px",
      maxHeight: "92vh",
      background: "#0d1117",
      border: "2px solid #1a2d1a",
      borderRadius: "32px",
      boxShadow:
        "0 0 50px rgba(0,245,160,0.08), inset 0 0 30px rgba(0,0,0,0.6)",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      zIndex: 2,
      animation: shake ? "shake 0.5s" : pulse ? "pulseGlow 0.6s" : "none",
    },
    bar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "14px 22px",
      borderBottom: "1px solid #1a2d1a",
      background: "#080d10",
      borderRadius: "32px 32px 0 0",
      flexShrink: 0,
    },
    barTitle: {
      fontSize: "11px",
      letterSpacing: "3px",
      color: "#00f5a0",
      fontWeight: "bold",
    },
    content: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "22px",
      opacity: animIn ? 1 : 0,
      transition: "opacity 0.18s ease",
      overflowY: "auto",
    },
    btn: (color = "#00f5a0") => ({
      padding: "14px",
      background: "transparent",
      border: `2px solid ${color}`,
      color,
      fontSize: "13px",
      fontWeight: "bold",
      letterSpacing: "2px",
      fontFamily: "inherit",
      cursor: "pointer",
      textTransform: "uppercase",
      borderRadius: "8px",
      width: "100%",
      transition: "all 0.18s ease",
    }),
    Kal: {
      background: "#0a1a12",
      border: "1px dashed #1a3a2a",
      borderRadius: "8px",
      padding: "12px 14px",
      display: "flex",
      gap: "12px",
      alignItems: "flex-start",
      marginBottom: "16px",
    },
    KalIcon: {
      fontSize: "22px",
      background: "#050a0f",
      borderRadius: "50%",
      padding: "4px 6px",
      border: "1px solid #1a3a2a",
      flexShrink: 0,
      marginTop: "2px",
    },
    card: {
      background: "#080d10",
      border: "1px solid #1a2d1a",
      borderRadius: "12px",
      padding: "18px",
      marginBottom: "16px",
    },
    statRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "10px 0",
      borderBottom: "1px solid #1a2d1a",
    },
  };

  const restartModal = showRestartAlert ? (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(5,10,15,0.85)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(4px)",
        borderRadius: "32px",
      }}
    >
      <div
        style={{
          background: "#0a1a12",
          border: "2px solid #f53b3b",
          borderRadius: "12px",
          padding: "24px",
          margin: "24px",
          textAlign: "center",
          boxShadow: "0 0 30px rgba(245,59,59,0.3)",
        }}
      >
        <div
          style={{
            fontSize: "36px",
            marginBottom: "12px",
            textShadow: "0 0 12px #f53b3b",
          }}
        >
          ⚠
        </div>
        <div
          style={{
            fontSize: "16px",
            color: "#f53b3b",
            fontWeight: "bold",
            letterSpacing: "2px",
            marginBottom: "10px",
          }}
        >
          ABORT MISSION?
        </div>
        <div
          style={{
            fontSize: "13px",
            color: "#a0c0b0",
            lineHeight: "1.6",
            marginBottom: "24px",
          }}
        >
          Are you sure you want to abort? All current progress and points will
          be permanently lost.
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            style={{ ...S.btn("#00f5a0"), flex: 1, padding: "12px" }}
            onMouseEnter={(e) => (e.target.style.background = "#00f5a022")}
            onMouseLeave={(e) => (e.target.style.background = "transparent")}
            onClick={() => setShowRestartAlert(false)}
          >
            CANCEL
          </button>
          <button
            style={{ ...S.btn("#f53b3b"), flex: 1, padding: "12px" }}
            onMouseEnter={(e) => (e.target.style.background = "#f53b3b22")}
            onMouseLeave={(e) => (e.target.style.background = "transparent")}
            onClick={() => {
              setShowRestartAlert(false);
              resetGame();
            }}
          >
            ABORT
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const leaderboardModal = showLeaderboardModal ? (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(5,10,15,0.85)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(4px)",
        borderRadius: "32px",
      }}
    >
      <div
        style={{
          background: "#0a1a12",
          border: "2px solid #00f5a0",
          borderRadius: "12px",
          padding: "24px",
          width: "80%",
          maxWidth: "320px",
          boxShadow: "0 0 30px rgba(0,245,160,0.2)",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            letterSpacing: "3px",
            color: "#00f5a0",
            marginBottom: "16px",
            textAlign: "center",
            fontWeight: "bold",
          }}
        >
          GLOBAL LEADERBOARD
        </div>
        {isFetchingLeaderboard ? (
          <div
            style={{
              textAlign: "center",
              color: "#4a7a5a",
              fontSize: "12px",
              padding: "20px 0",
            }}
          >
            Decrypting Data...
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              marginBottom: "24px",
              maxHeight: "45vh",
              overflowY: "auto",
              paddingRight: "8px",
            }}
          >
            {globalLeaderboard.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#4a7a5a",
                  fontSize: "12px",
                }}
              >
                No operatives found.
              </div>
            ) : (
              globalLeaderboard.map((entry, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "13px",
                  }}
                >
                  <div style={{ display: "flex", gap: "8px" }}>
                    <span
                      style={{
                        color:
                          i === 0
                            ? "#f5c400"
                            : i === 1
                              ? "#cbd5e1"
                              : i === 2
                                ? "#b45309"
                                : "#4a7a5a",
                        fontWeight: "bold",
                        width: "18px",
                      }}
                    >
                      #{i + 1}
                    </span>
                    <span
                      style={{
                        color:
                          entry.operative_name === operativeName
                            ? "#00f5a0"
                            : "#a0c0b0",
                        fontWeight:
                          entry.operative_name === operativeName
                            ? "bold"
                            : "normal",
                      }}
                    >
                      {entry.operative_name}
                    </span>
                  </div>
                  <span
                    style={{
                      color:
                        entry.operative_name === operativeName
                          ? "#00f5a0"
                          : "#6a8a7a",
                      fontWeight: "bold",
                    }}
                  >
                    {entry.score}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
        <button
          style={{ ...S.btn("#00f5a0"), padding: "10px" }}
          onMouseEnter={(e) => (e.target.style.background = "#00f5a022")}
          onMouseLeave={(e) => (e.target.style.background = "transparent")}
          onClick={() => setShowLeaderboardModal(false)}
        >
          CLOSE
        </button>
      </div>
    </div>
  ) : null;

  const creditsModal = showCreditsModal ? (
    <div
      style={{
        position: "absolute", inset: 0, background: "rgba(5,10,15,0.85)", zIndex: 100,
        display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)", borderRadius: "32px",
      }}
    >
      <div
        style={{
          background: "#0a1a12", border: "2px solid #00f5a0", borderRadius: "12px", padding: "24px",
          width: "80%", maxWidth: "280px", textAlign: "center", boxShadow: "0 0 30px rgba(0,245,160,0.2)",
        }}
      >
        <div style={{ fontSize: "14px", letterSpacing: "2px", color: "#00f5a0", marginBottom: "16px", fontWeight: "bold" }}>
          MISSION ORIGIN
        </div>
        
        <div style={{ fontSize: "12px", color: "#a0c0b0", lineHeight: "1.6", marginBottom: "16px" }}>
          Created by <strong style={{ color: "#00f5a0" }}>Muhammad Uthman</strong><br />
          Year 12, Irfan College<br /><br />
          Developed for the<br />
          <em style={{ color: "#f5c400" }}>The Tax, Super + You</em> competition.
        </div>

        {/* --- FAVICON CREDIT SECTION --- */}
        <div style={{ 
          fontSize: "9px", 
          color: "#4a7a5a", 
          borderTop: "1px solid #1a3a2a", 
          paddingTop: "12px", 
          marginBottom: "24px" 
        }}>
          <a 
            href="https://www.flaticon.com/free-icons/defense" 
            title="defense icons" 
            target="_blank" 
            rel="noreferrer"
            style={{ color: "#4a7a5a", textDecoration: "none" }}
          >
            Defense icon by Ian Anandara - Flaticon
          </a>
        </div>
        {/* ------------------------------- */}

        <button
          style={{ ...S.btn("#00f5a0"), padding: "10px" }}
          onMouseEnter={(e) => (e.target.style.background = "#00f5a022")}
          onMouseLeave={(e) => (e.target.style.background = "transparent")}
          onClick={() => setShowCreditsModal(false)}
        >
          CLOSE
        </button>
      </div>
    </div>
  ) : null;

  if (screen === "name") {
    return (
      <div style={S.root}>
        <div style={S.grid} />
        <div style={S.phone}>
          <div style={S.bar}>
            <span style={S.barTitle}>
              {screen === "name"
                ? "SYSTEM ACCESS"
                : screen === "results"
                  ? "MISSION COMPLETE"
                  : `OutsmartyScam: OP. ${operativeName || "UNKNOWN"}`}
            </span>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {/* New Trophy Button */}
              <button
                onClick={openLeaderboard}
                title="Global Leaderboard"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f5c400",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                🏆
              </button>
              <button
  onClick={() => setShowCreditsModal(true)}
  title="Credits"
  style={{
    background: "none", border: "none", color: "#4a7a5a", fontSize: "16px", cursor: "pointer", padding: 0,
  }}
>
  ℹ️
</button>
            </div>
          </div>
          <div
            style={{
              ...S.content,
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <h2
              style={{
                fontSize: "20px",
                color: "#00f5a0",
                letterSpacing: "2px",
                marginBottom: "24px",
              }}
            >
              ENTER OPERATIVE NAME
            </h2>
            <input
              type="text"
              maxLength={16}
              value={operativeName}
              onChange={(e) => {
                setOperativeName(
                  e.target.value.replace(/\s/g, "").toUpperCase(),
                );
                setNameError("");
              }}
              placeholder=""
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: "#080d10",
                border: "2px solid #00f5a0",
                color: "#00f5a0",
                fontSize: "24px",
                padding: "16px",
                textAlign: "center",
                fontFamily: "inherit",
                textTransform: "uppercase",
                letterSpacing: "4px",
                borderRadius: "8px",
                marginBottom: "32px",
                outline: "none",
              }}
            />
            {nameError && (
              <div
                style={{
                  color: "#f53b3b",
                  fontSize: "12px",
                  marginBottom: "16px",
                  fontWeight: "bold",
                  letterSpacing: "1px",
                }}
              >
                {nameError}
              </div>
            )}
            <button
              style={{
                ...S.btn(),
                opacity: operativeName.length > 0 ? 1 : 0.4,
                pointerEvents: operativeName.length > 0 ? "auto" : "none",
              }}
              onClick={handleAuthenticate} // Use the new DB check function
            >
              {isVerifying ? "VERIFYING..." : "AUTHENTICATE →"}
            </button>
          </div>
          {restartModal}
          {leaderboardModal}
          {creditsModal}
        </div>
      </div>
    );
  }

  if (screen === "intro")
    return (
      <div style={S.root}>
        <div style={S.grid} />
        <style>{`
        @keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-10px)}40%{transform:translateX(10px)}60%{transform:translateX(-5px)}80%{transform:translateX(5px)}}
        @keyframes pulseGlow{0%{box-shadow:0 0 0 0 rgba(0,245,160,0.6),0 0 50px rgba(0,245,160,0.08)}70%{box-shadow:0 0 0 20px rgba(0,245,160,0),0 0 50px rgba(0,245,160,0.08)}100%{box-shadow:0 0 0 0 rgba(0,245,160,0),0 0 50px rgba(0,245,160,0.08)}}
        @keyframes blinkCursor{0%,100%{opacity:1}50%{opacity:0}}
      `}</style>
        <div style={S.phone}>
          <div style={S.bar}>
            <span style={S.barTitle}>
              {screen === "name"
                ? "SYSTEM ACCESS"
                : screen === "results"
                  ? "MISSION COMPLETE"
                  : `OutsmartyScam: OP. ${operativeName || "UNKNOWN"}`}
            </span>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {/* New Trophy Button */}
              <button
                onClick={openLeaderboard}
                title="Global Leaderboard"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f5c400",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                🏆
              </button>
              <button
  onClick={() => setShowCreditsModal(true)}
  title="Credits"
  style={{
    background: "none", border: "none", color: "#4a7a5a", fontSize: "16px", cursor: "pointer", padding: 0,
  }}
>
  ℹ️
</button>
            </div>
          </div>
          <div style={{ ...S.content, justifyContent: "space-between" }}>
            <div>
              <h1
                style={{
                  fontSize: "38px",
                  color: "#00f5a0",
                  margin: "0 0 8px 0",
                  lineHeight: 1.05,
                  textShadow: "0 0 12px rgba(0,245,160,0.4)",
                  minHeight: "85px", // Reduced height since it's only 2 lines now
                }}
              >
                <Typewriter
                  text={`WELCOME,\nOP. ${operativeName.toUpperCase()}`}
                  speed={40}
                />
                <span style={{ animation: "blinkCursor 1s infinite" }}>_</span>
              </h1>
              <div
                style={{
                  height: "2px",
                  background: "linear-gradient(90deg, #00f5a0, transparent)",
                  marginBottom: "22px",
                }}
              />
              <div style={S.Kal}>
                <div style={S.KalIcon}>🕵️</div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#a0c0b0",
                    lineHeight: "1.6",
                  }}
                >
                  <Typewriter
                    text={`I'm Agent Kal, your handler. Scammers are actively targeting Australian taxpayers. Your mission: review 12 intercepted comms. You have 60 seconds per message to classify it: legitimate ATO correspondence, or a scam. Speed and accuracy will determine your final rank. Stay sharp.`}
                    speed={16}
                  />
                </div>
              </div>
              <div style={{ ...S.card, padding: "14px" }}>
                <div
                  style={{
                    fontSize: "9px",
                    letterSpacing: "3px",
                    color: "#1a5a3a",
                    marginBottom: "10px",
                  }}
                >
                  SCORING RULES
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6a8a7a",
                    lineHeight: "1.8",
                  }}
                >
                  ✓ Correct answer —{" "}
                  <strong style={{ color: "#00f5a0" }}>+100 PTS</strong>
                  <br />⏱ Time Bonus —{" "}
                  <strong style={{ color: "#00f5a0" }}>Up to +100 PTS</strong>
                  <br />
                  🔥 Streak (3+) —{" "}
                  <strong style={{ color: "#00f5a0" }}>
                    +25 PTS per Answer
                  </strong>
                  <br />❌ Wrong/Timeout —{" "}
                  <strong style={{ color: "#f53b3b" }}>
                    0 PTS & Streak Breaks
                  </strong>
                </div>
              </div>
            </div>
            <button
              style={S.btn()}
              onMouseEnter={(e) => {
                e.target.style.background = "#00f5a0";
                e.target.style.color = "#050a0f";
                e.target.style.boxShadow = "0 0 20px rgba(0,245,160,0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#00f5a0";
                e.target.style.boxShadow = "none";
              }}
              onClick={handleStart}
            >
              ▶ INITIATE TRAINING
            </button>
          </div>
          {restartModal}
          {leaderboardModal}
          {creditsModal}
        </div>
      </div>
    );

  if (screen === "roundbreak") {
    const nr = SCENARIOS[currentIndex].round;
    const descs = [
      "",
      "Round 1 cleared. Scammers are getting smarter — stay sharp.",
      "Halfway through. The next round is expert level.",
      "",
    ];
    return (
      <div style={S.root}>
        <div style={S.grid} />
        <div style={S.phone}>
          <div style={S.bar}>
            <span style={S.barTitle}>ROUND COMPLETE</span>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <span
                style={{
                  color: "#00f5a0",
                  fontSize: "13px",
                  fontWeight: "bold",
                }}
              >
                {score} PTS {streak >= 3 ? `🔥${streak}` : ""}
              </span>
              <button
                onClick={openLeaderboard}
                title="Global Leaderboard"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f5c400",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                🏆
              </button>
              <button
  onClick={() => setShowCreditsModal(true)}
  title="Credits"
  style={{
    background: "none", border: "none", color: "#4a7a5a", fontSize: "16px", cursor: "pointer", padding: 0,
  }}
>
  ℹ️
</button>
              <button
                onClick={handleRestart}
                title="Restart Mission"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f53b3b",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ↺
              </button>
            </div>
          </div>
          <div
            style={{
              ...S.content,
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "110px",
                color: "#00f5a0",
                opacity: 0.07,
                fontWeight: "bold",
                lineHeight: 1,
                marginBottom: "-50px",
              }}
            >
              {nr}
            </div>
            <h2
              style={{
                fontSize: "26px",
                color: "#00f5a0",
                letterSpacing: "4px",
                margin: "0 0 8px 0",
              }}
            >
              ROUND {nr}
            </h2>
            <h3
              style={{
                fontSize: "11px",
                color: "#4a7a5a",
                letterSpacing: "3px",
                margin: "0 0 24px 0",
              }}
            >
              {ROUND_LABELS[nr - 1]}
            </h3>
            <p
              style={{
                fontSize: "13px",
                color: "#8aaa9a",
                lineHeight: "1.7",
                maxWidth: "280px",
                fontFamily: "'Courier New', monospace",
              }}
            >
              {descs[nr - 1]}
            </p>
            <button
              style={{
                ...S.btn(),
                marginTop: "36px",
                width: "auto",
                padding: "14px 40px",
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#00f5a0";
                e.target.style.color = "#050a0f";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#00f5a0";
              }}
              onClick={() => setScreen("game")}
            >
              CONTINUE →
            </button>
          </div>
          {restartModal}
          {leaderboardModal}
          {creditsModal} 
          {}
        </div>
      </div>
    );
  }

  if (screen === "game") {
    const tc = getTypeColor(current.type);

    // Timer colors
    let timerColor = "#00f5a0";
    if (timeLeft <= 15) timerColor = "#f5c400";
    if (timeLeft <= 5) timerColor = "#f53b3b";

    return (
      <div style={S.root}>
        <div style={S.grid} />
        <div style={S.phone}>
          <div style={S.bar}>
            <span style={S.barTitle}>OP: {operativeName.toUpperCase()}</span>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <span
                style={{
                  color: "#00f5a0",
                  fontSize: "13px",
                  fontWeight: "bold",
                }}
              >
                {score} PTS {streak >= 3 ? `🔥${streak}` : ""}
              </span>
              <button
                onClick={openLeaderboard}
                title="Global Leaderboard"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f5c400",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                🏆
              </button>
              <button
  onClick={() => setShowCreditsModal(true)}
  title="Credits"
  style={{
    background: "none", border: "none", color: "#4a7a5a", fontSize: "16px", cursor: "pointer", padding: 0,
  }}
>
  ℹ️
</button>
              <button
                onClick={handleRestart}
                title="Restart Mission"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f53b3b",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ↺
              </button>
            </div>
          </div>

          <div style={{ height: "4px", background: "#1a2d1a", width: "100%" }}>
            <div
              style={{
                height: "100%",
                width: `${(timeLeft / 60) * 100}%`,
                background: timerColor,
                transition: "width 1s linear, background-color 0.5s",
              }}
            />
          </div>

          <div style={S.content}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  padding: "3px 10px",
                  border: "1px solid #1a3a2a",
                  fontSize: "9px",
                  letterSpacing: "3px",
                  color: "#00a066",
                  borderRadius: "2px",
                  background: "#050f08",
                }}
              >
                ROUND {current.round} · {currentIndex + 1}/{total}
              </div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: timerColor,
                  fontFamily: "'Courier New', monospace",
                }}
              >
                00:{timeLeft < 10 ? `0${timeLeft}` : timeLeft}
              </div>
            </div>

            {/* Message card */}
            <div style={{ ...S.card, flex: 1 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "14px",
                  paddingBottom: "12px",
                  borderBottom: "1px solid #1a2d1a",
                }}
              >
                <span style={{ fontSize: "16px" }}>
                  {getTypeIcon(current.type)}
                </span>
                <span
                  style={{
                    fontSize: "10px",
                    letterSpacing: "1px",
                    padding: "3px 8px",
                    borderRadius: "4px",
                    background: tc + "22",
                    color: tc,
                    border: `1px solid ${tc}44`,
                    fontWeight: "bold",
                  }}
                >
                  {current.type}
                </span>
                <span
                  style={{
                    fontSize: "11px",
                    color: "#4a7a5a",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {current.sender}
                </span>
              </div>
              {current.subject && (
                <div
                  style={{
                    fontSize: "13px",
                    color: "#c8e8d8",
                    fontWeight: "bold",
                    marginBottom: "10px",
                  }}
                >
                  {current.subject}
                </div>
              )}
              <div
                style={{
                  fontSize: "13px",
                  color: "#8aaa9a",
                  lineHeight: "1.7",
                }}
              >
                {current.content}
              </div>
            </div>

            {/* Kal hint */}
            <div style={S.Kal}>
              <div style={S.KalIcon}>🕵️</div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#7a9a8a",
                  fontStyle: "italic",
                  lineHeight: "1.5",
                }}
              >
                <Typewriter text={current.hint} speed={15} />
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                style={{ ...S.btn("#f53b3b"), flex: 1 }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#f53b3b22";
                  e.target.style.boxShadow = "0 0 14px rgba(245,59,59,0.25)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.boxShadow = "none";
                }}
                onClick={() => handleAnswer(true)}
              >
                ⚠ SCAM
              </button>
              <button
                style={{ ...S.btn("#00f5a0"), flex: 1 }}
                onMouseEnter={(e) => {
                  e.target.style.background = "#00f5a022";
                  e.target.style.boxShadow = "0 0 14px rgba(0,245,160,0.25)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = "transparent";
                  e.target.style.boxShadow = "none";
                }}
                onClick={() => handleAnswer(false)}
              >
                ✓ REAL
              </button>
            </div>
          </div>
          {restartModal}
          {leaderboardModal}
          {creditsModal} 
        </div>
      </div>
    );
  }

  if (screen === "feedback") {
    const correct = lastCorrect;
    return (
      <div style={S.root}>
        <div style={S.grid} />
        <div style={S.phone}>
          <div style={S.bar}>
            <span style={S.barTitle}>OP: {operativeName.toUpperCase()}</span>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              <span
                style={{
                  color: "#00f5a0",
                  fontSize: "13px",
                  fontWeight: "bold",
                }}
              >
                {score} PTS {streak >= 3 ? `🔥${streak}` : ""}
              </span>
              <button
                onClick={openLeaderboard}
                title="Global Leaderboard"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f5c400",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                🏆
              </button>
              <button
  onClick={() => setShowCreditsModal(true)}
  title="Credits"
  style={{
    background: "none", border: "none", color: "#4a7a5a", fontSize: "16px", cursor: "pointer", padding: 0,
  }}
>
  ℹ️
</button>
              <button
                onClick={handleRestart}
                title="Restart Mission"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f53b3b",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ↺
              </button>
            </div>
          </div>
          <div style={S.content}>
            {/* Result banner */}
            <div
              style={{
                padding: "18px",
                borderRadius: "12px",
                background: correct ? "#001a0d" : "#1a0008",
                border: `2px solid ${correct ? "#00f5a0" : "#f53b3b"}`,
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "14px",
              }}
            >
              <div
                style={{
                  fontSize: "30px",
                  textShadow: `0 0 12px ${correct ? "#00f5a0" : "#f53b3b"}`,
                }}
              >
                {isTimeout ? "⏳" : correct ? "✅" : "❌"}
              </div>
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: correct ? "#00f5a0" : "#f53b3b",
                    letterSpacing: "2px",
                    marginBottom: "4px",
                  }}
                >
                  {isTimeout ? "TIME OUT!" : correct ? "CORRECT!" : "HACKED!"}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: correct ? "#00a066" : "#a02040",
                    letterSpacing: "1px",
                  }}
                >
                  That was a{" "}
                  <strong>
                    {current.isScam ? "SCAM" : "LEGITIMATE NOTIFICATION"}
                  </strong>
                </div>
              </div>
              {correct && lastScoreBreakdown && (
                <div
                  style={{
                    textAlign: "right",
                    fontSize: "10px",
                    color: "#00f5a0",
                    lineHeight: "1.4",
                  }}
                >
                  <div>Base: +{lastScoreBreakdown.base}</div>
                  <div>Time: +{lastScoreBreakdown.time}</div>
                  {lastScoreBreakdown.streak > 0 && (
                    <div>Streak: +{lastScoreBreakdown.streak}</div>
                  )}
                  <div
                    style={{
                      fontWeight: "bold",
                      borderTop: "1px solid #00f5a0",
                      marginTop: "2px",
                      paddingTop: "2px",
                    }}
                  >
                    +{lastScoreBreakdown.total}
                  </div>
                </div>
              )}
            </div>

            {/* Kal debrief */}
            <div style={{ ...S.card, flex: 1, marginBottom: "14px" }}>
              <div
                style={{
                  fontSize: "9px",
                  letterSpacing: "3px",
                  color: "#1a5a3a",
                  marginBottom: "12px",
                }}
              >
                AGENT Kal'S DEBRIEF
              </div>
              <p
                style={{
                  fontSize: "13px",
                  color: "#a0c0b0",
                  lineHeight: "1.75",
                  margin: 0,
                }}
              >
                <Typewriter text={current.explanation} speed={14} />
              </p>
            </div>

            {/* Protection tip */}
            <div
              style={{
                background: "#0a1a12",
                border: "1px solid #1a3a2a",
                borderRadius: "8px",
                padding: "12px 14px",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  fontSize: "9px",
                  letterSpacing: "3px",
                  color: "#00a066",
                  marginBottom: "6px",
                }}
              >
                PROTECTION TIP
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#5a8a6a",
                  lineHeight: "1.6",
                  fontFamily: "'Courier New', monospace",
                }}
              >
                {current.tip}
              </div>
            </div>

            {current.intelLink && (
              <div style={{ marginBottom: "14px", textAlign: "center" }}>
                <a
                  href={current.intelLink.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    fontSize: "11px",
                    color: "#00f5a0",
                    textDecoration: "none",
                    borderBottom: "1px solid #00f5a0",
                    paddingBottom: "2px",
                    fontFamily: "'Courier New', monospace",
                  }}
                >
                  📂 VIEW INTEL: {current.intelLink.label}
                </a>
              </div>
            )}
            <button
              style={S.btn()}
              onMouseEnter={(e) => {
                e.target.style.background = "#00f5a0";
                e.target.style.color = "#050a0f";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#00f5a0";
              }}
              onClick={handleNext}
            >
              {currentIndex + 1 >= total
                ? "VIEW FINAL RESULTS →"
                : "NEXT COMM →"}
            </button>
          </div>
          {restartModal}
          {leaderboardModal}
          {creditsModal} 
        </div>
      </div>
    );
  }

  if (screen === "results") {
    const rank = getRank();
    const correctAns = answers.filter((a) => a.correct).length;
    return (
      <div style={S.root}>
        <div style={S.grid} />
        <div style={S.phone}>
          <div style={S.bar}>
            <span style={S.barTitle}>
              {screen === "name"
                ? "SYSTEM ACCESS"
                : screen === "results"
                  ? "MISSION COMPLETE"
                  : `OutsmartyScam: OP. ${operativeName || "UNKNOWN"}`}
            </span>
            <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
              {/* New Trophy Button */}
              <button
                onClick={openLeaderboard}
                title="Global Leaderboard"
                style={{
                  background: "none",
                  border: "none",
                  color: "#f5c400",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                🏆
              </button>
              <button
  onClick={() => setShowCreditsModal(true)}
  title="Credits"
  style={{
    background: "none", border: "none", color: "#4a7a5a", fontSize: "16px", cursor: "pointer", padding: 0,
  }}
>
  ℹ️
</button>
            </div>
          </div>
          <div style={S.content}>
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
              <div
                style={{
                  fontSize: "9px",
                  letterSpacing: "4px",
                  color: "#1a5a3a",
                  marginBottom: "8px",
                }}
              >
                FINAL RANKING
              </div>
              <div
                style={{
                  fontSize: "26px",
                  color: rank.color,
                  fontWeight: "bold",
                  letterSpacing: "3px",
                  textShadow: `0 0 14px ${rank.color}55`,
                  marginBottom: "6px",
                }}
              >
                {rank.title}
              </div>
              <div
                style={{
                  fontSize: "56px",
                  color: "#00f5a0",
                  fontWeight: "bold",
                  lineHeight: 1,
                  marginBottom: "4px",
                }}
              >
                {score}
              </div>
              <div
                style={{
                  fontSize: "9px",
                  color: "#2a4a3a",
                  letterSpacing: "3px",
                  marginBottom: "12px",
                }}
              >
                TOTAL POINTS
              </div>
              <p
                style={{
                  fontSize: "12px",
                  color: "#7a9a8a",
                  lineHeight: "1.6",
                  margin: 0,
                }}
              >
                {rank.desc}
              </p>
            </div>

            {/* Stats */}
            <div style={{ ...S.card, marginBottom: "14px" }}>
              <div style={S.statRow}>
                <span
                  style={{
                    fontSize: "10px",
                    color: "#4a7a5a",
                    letterSpacing: "2px",
                  }}
                >
                  OPERATIVE
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#00f5a0",
                    fontWeight: "bold",
                  }}
                >
                  {operativeName.toUpperCase()}
                </span>
              </div>
              <div style={S.statRow}>
                <span
                  style={{
                    fontSize: "10px",
                    color: "#4a7a5a",
                    letterSpacing: "2px",
                  }}
                >
                  ACCURACY
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#00f5a0",
                    fontWeight: "bold",
                  }}
                >
                  {Math.round((correctAns / total) * 100)}%
                </span>
              </div>
              <div style={S.statRow}>
                <span
                  style={{
                    fontSize: "10px",
                    color: "#4a7a5a",
                    letterSpacing: "2px",
                  }}
                >
                  BEST STREAK
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#00f5a0",
                    fontWeight: "bold",
                  }}
                >
                  {maxStreak} 🔥
                </span>
              </div>
              <div style={{ ...S.statRow, borderBottom: "none" }}>
                <span
                  style={{
                    fontSize: "10px",
                    color: "#4a7a5a",
                    letterSpacing: "2px",
                  }}
                >
                  SCAMS CAUGHT
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#00f5a0",
                    fontWeight: "bold",
                  }}
                >
                  {answers.filter((a) => a.isScam && a.correct).length} /{" "}
                  {SCENARIOS.filter((s) => s.isScam).length}
                </span>
              </div>
            </div>

            {/* LEADERBOARD */}
            <div style={{ ...S.card, marginBottom: "16px", padding: "14px" }}>
              <div
                style={{
                  fontSize: "10px",
                  letterSpacing: "3px",
                  color: "#00a066",
                  marginBottom: "12px",
                  textAlign: "center",
                  borderBottom: "1px solid #1a3a2a",
                  paddingBottom: "8px",
                }}
              >
                GLOBAL LEADERBOARD
              </div>
              {isSubmitting ? (
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "12px",
                    color: "#4a7a5a",
                    padding: "10px",
                  }}
                >
                  Syncing Intel...
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {leaderboard.map((entry, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "12px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            color:
                              i === 0
                                ? "#f5c400"
                                : i === 1
                                  ? "#cbd5e1"
                                  : i === 2
                                    ? "#b45309"
                                    : "#4a7a5a",
                            fontWeight: "bold",
                            width: "16px",
                          }}
                        >
                          #{i + 1}
                        </span>
                        <span
                          style={{
                            color:
                              entry.operative_name === operativeName
                                ? "#00f5a0"
                                : "#a0c0b0",
                            fontWeight:
                              entry.operative_name === operativeName
                                ? "bold"
                                : "normal",
                          }}
                        >
                          {entry.operative_name}
                        </span>
                      </div>
                      <span
                        style={{
                          color:
                            entry.operative_name === operativeName
                              ? "#00f5a0"
                              : "#6a8a7a",
                          fontWeight: "bold",
                        }}
                      >
                        {entry.score}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              style={S.btn()}
              onMouseEnter={(e) => {
                e.target.style.background = "#00f5a0";
                e.target.style.color = "#050a0f";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "transparent";
                e.target.style.color = "#00f5a0";
              }}
              onClick={resetGame}
            >
              ↺ PLAY AGAIN
            </button>
          </div>
          {restartModal}
          {leaderboardModal}
          {creditsModal} 
        </div>
      </div>
    );
  }

  return null;
}
